// @angular
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { forkJoin } from "rxjs";
// Translate
import { TranslateService, TranslateModule } from "@ngx-translate/core";
// Own services
import { ToastService } from "../../../services/shared/ToastService.service";
import { UserControllerService } from "../../../services/server/UserController.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { GraphControllerComponent } from "../../graph-module/graph-controller/graph-controller.component";
import { NotificationModalService } from "../../notification-module/notification-service/notification-modal.service";
import { DateParserService } from "../../../services/shared/DateParserService.service";
// Variables
import { GRAPH_CONFIG } from "../../graph-module/GRAPH_CONFIG";
import { METROLOGY_TYPE } from "../../../interfaces/DeviceGlobalInterface.type";
import { GRAPH_TYPES } from "../../../screens/dashboard/devices/devices-common-components/device-consumption-graph/device-consumption-graph.component";
// Interfaces
import { MaterialSelectConfig } from "../../material-module/MaterialInterface.type";
import { DateRange } from "../../../interfaces/DatesGlobalInterface.type";
import { GraphSeries } from "../../../interfaces/GraphGlobalInterface.type";
import { THEME_COLORS } from "../../../themes/THEME_COLORS";
import { SCROLLBAR_OPTIONS } from "../../graph-module/GraphInterface.type";
import { MAP_TYPES_NAMES } from "../../map-module/map-variables/MAP_TYPES";
// Components
import { MaterialSelectComponent } from "../../material-module/material-select/material-select.component";
import { MapControllerComponent } from "../../map-module/map-controller/map-controller.component";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { DataProtectedPipe } from "../../../pipes/data-protected.pipe";

/**
 * Subscriber CUPS detail: map, readings table and consumption graph (index, consumption, normalized)
 * for the selected CUPS and its meters. Inputs: cupsList, cupsData.
 * Readings are fetched with UserController.getReadings and getMediaReadings.
 */
@Component({
  selector: "app-subscriber-cups",
  templateUrl: "./subscriber-cups.component.html",
  styleUrls: ["./subscriber-cups.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DataProtectedPipe,
    MapControllerComponent,
    NgClass,
    MaterialSelectComponent,
    MatCheckboxModule,
    FormsModule,
    GraphControllerComponent,
    TranslateModule
  ]
})
export class SubscriberCupsComponent implements OnInit, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Chart initialized
  graphInitiated = false;
  currentColors: object;

  // CUPS
  @Input() cupsList: any;
  @Input()
  get cupsData(): any {
    return this._cupsData;
  }
  set cupsData(cupsData: any) {
    if (cupsData) {
      this._cupsData = cupsData;
      this.SessionDataService.sendAgrupation({
        entity: null,
        id: null,
        name: null,
        showAllEntity: null,
        virtual: null,
        timezone: this._cupsData.timeZone ? this._cupsData.timeZone : "Europe/Madrid"
      });
      this.selectedMeterId = this.cupsList[this.currentCupsIndex]?.contadoresList[0]?.id;
      this.initialMeterSelection = this.selectedMeterId;
      this.updateGraphFilters(this.cupsList[this.currentCupsIndex]?.contadoresList[0]);
      setTimeout(() => this.getCupsTable(this._cupsData), 0);

      if (this.graphInitiated) {
        this.highchartsOptions = null;
        this.loadGraph();
        this.loadGraphData();
      } else {
        this.loadGraph();
      }
    }
  }
  _cupsData: any;
  currentCupsId: number;
  currentCupsIndex: number;
  selectedMeterId: number;
  initialMeterSelection: number;
  @ViewChild("cupsContainer") cupsContainer: ElementRef;

  // Data table
  tableData: object[];

  // Map
  mapType: string = MAP_TYPES_NAMES.METER_LIST;
  mapHeight: number = 300;
  initialZoom: number = 17;

  // Chart
  graphSeries: GraphSeries[];
  graphData: object[];
  readonly GRAPH_TYPES = GRAPH_TYPES;
  graphType: number = GRAPH_TYPES.CONSUMPTION;
  graphFilters: MaterialSelectConfig[] = [
    {
      title: this.translate.instant("type"),
      options: [
        { value: GRAPH_TYPES.INDEX, name: this.translate.instant("index") },
        {
          value: GRAPH_TYPES.CONSUMPTION,
          name: this.translate.instant("hour-consumption")
        },
        {
          value: GRAPH_TYPES.NORMALIZED_CONSUMPTION,
          name: this.translate.instant("consumption-normalized")
        }
      ],
      selected: GRAPH_TYPES.CONSUMPTION
    }
  ];
  highchartsOptions: object;
  chartOptions: object;
  chartConstructor: string = "stockChart";
  defaultDateRange: DateRange = this.DateParserService.getLastDays(7);
  @ViewChild(GraphControllerComponent)
  GraphController: GraphControllerComponent;
  from: string;
  to: string;

  // Custom alarms
  showAlarmsConfig = false;
  sessionUser: string;

  // Average values
  energyMedia: boolean = false;
  averageData: object[];

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly DateParserService: DateParserService,
    public notificationModal: NotificationModalService,
    private readonly SessionDataService: SessionDataService,
    private readonly UserController: UserControllerService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.currentColors = untracked(() => this.SessionDataService.colors());
    this.sessionUser = untracked(() => this.SessionDataService.user());
    this.loadComponent();
  }

  /***************************************************************************/
  // ANCHOR After-render execution
  /***************************************************************************/

  ngAfterViewInit(): void {
    this.cupsContainer.nativeElement.style.setProperty("--cups-map-index", 55);
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Select the first CUPS and its first meter, and publish the CUPS id to session
  loadComponent(): void {
    this.currentCupsId = this.cupsList[0]?.id;
    this.currentCupsIndex = 0;
    this.selectedMeterId = this.cupsList[this.currentCupsIndex]?.contadoresList[0]?.id;
    this.SessionDataService.sendSubscriberCups(this.currentCupsId);
    this.initialMeterSelection = this.selectedMeterId;
  }

  /***************************************************************************/
  // ANCHOR Meter cards
  /***************************************************************************/

  // Build the summary cards (tableData) for the selected meter
  getCupsTable(cupsData: any): void {
    const selectedMeterNroSerie = this.cupsList[this.currentCupsIndex]?.contadoresList.find(
      meter => meter.id === this.selectedMeterId
    )?.nroSerie;

    const selectedMeter = cupsData.contadores.find(
      meter => meter.nroSerie === selectedMeterNroSerie
    );

    const unit = selectedMeter?.metrologyType === METROLOGY_TYPE.WATER ? " m³" : " Nm³";

    const safeValue = (value: any): string =>
      value != null ? value + unit : this.translate.instant("unknown");

    this.tableData = [
      {
        text: "last-know-value",
        subText:
          " (" +
          (selectedMeter?.lastReadedTimestampParsed || this.translate.instant("unknown")) +
          ")",
        data:
          selectedMeter?.lastReadedValue != null
            ? selectedMeter.lastReadedValue + " m³"
            : this.translate.instant("unknown")
      },
      {
        text: "last-data-month",
        data: safeValue(selectedMeter?.consumptionCards.lastMonthlyValue)
      },
      {
        text: "last-month-consumption",
        data: safeValue(selectedMeter?.consumptionCards.lastMonth)
      },
      {
        text: "consumption-month-average",
        data: safeValue(selectedMeter?.consumptionCards.monthlyAverage)
      },
      {
        text: "current-month-consumption",
        data: safeValue(selectedMeter?.consumptionCards.currentMonth)
      },
      {
        text: "consumption-average-daily",
        data: safeValue(selectedMeter?.consumptionCards.dailyAverage)
      }
    ];
  }

  /***************************************************************************/
  // ANCHOR Chart
  /***************************************************************************/

  // Set the base Highcharts options; loadGraphData fills in the data
  loadGraph(): void {
    this.setHighchartsOptions();
  }

  // Fetch readings for the chart; defaults to the last 7 days when no range is given
  loadGraphData(from?: any, to?: any): void {
    if (!from || !to) {
      this.from = this.DateParserService.getDateLocalTimestamp(
        this.defaultDateRange.startDate,
        this._cupsData.timeZone ? this._cupsData.timeZone : "Europe/Madrid"
      ).toString();
      this.to = this.DateParserService.getDateLocalTimestamp(
        this.defaultDateRange.endDate,
        this._cupsData.timeZone ? this._cupsData.timeZone : "Europe/Madrid"
      ).toString();
    } else {
      this.from = from;
      this.to = to;
    }

    let requests = [
      this.UserController.getReadings({
        claveMaestraId: this.currentCupsId,
        type: this.graphType,
        meter: this.selectedMeterId,
        from: this.from,
        to: this.to
      })
    ];

    if (this.energyMedia) {
      requests.push(
        this.UserController.getMediaReadings({
          claveMaestraId: this.currentCupsId,
          type: this.graphType,
          meter: this.selectedMeterId,
          from: this.from,
          to: this.to
        })
      );
    }

    forkJoin(requests).subscribe(responses => {
      let graphData: any[] = [];
      let averageData: any[] = [];

      if (responses[0]["code"] === 0 && responses[0]["body"]) {
        graphData = responses[0]["body"];
      }

      if (this.energyMedia && responses[1]["code"] === 0 && responses[1]["body"]) {
        averageData = responses[1]["body"];
      }

      this.graphData = graphData;
      this.averageData = averageData;
      this.getSeries();
    });
  }

  // Build chart series from readings and average values
  getSeries(): void {
    let graphSeries: GraphSeries[] = [];
    let nameText: string;

    if (this.graphType === GRAPH_TYPES.INDEX) {
      nameText = this.translate.instant("value");
    } else if (this.graphType === GRAPH_TYPES.CONSUMPTION) {
      nameText = this.translate.instant("consumption");
    } else {
      nameText = this.translate.instant("consumption-normalized");
    }

    // Readings
    graphSeries[0] = {
      id: "valor",
      name: nameText,
      type: this.graphType === GRAPH_TYPES.INDEX ? "line" : "area",
      data: this.graphData,
      tooltip: {
        valueSuffix:
          this.graphType === GRAPH_TYPES.INDEX || this.graphType === GRAPH_TYPES.CONSUMPTION
            ? " m³"
            : " Nm³",
        valueDecimals: 3
      },
      color: this.currentColors[THEME_COLORS.BLUE_LIGHT],
      navigatorOptions: {
        type: this.graphType === GRAPH_TYPES.INDEX ? "line" : "area"
      }
    };

    // Average values
    if (this.energyMedia) {
      graphSeries[1] = {
        id: "average",
        name: this.translate.instant("average"),
        type: "line",
        data: this.averageData,
        tooltip: {
          valueSuffix: this.graphType === GRAPH_TYPES.CONSUMPTION ? " m³" : " Nm³",
          valueDecimals: 3
        },
        color: this.currentColors[THEME_COLORS.BLUE_DARK],
        showInNavigator: false
      };
    }

    this.graphSeries = graphSeries;
    this.setChartsOptions();
  }

  // Build the Highcharts stock options from GRAPH_CONFIG
  setHighchartsOptions(): void {
    let highchartsOptions: object = JSON.parse(JSON.stringify(GRAPH_CONFIG.default.options));

    highchartsOptions["tooltip"] = {
      enabled: true,
      shared: true,
      split: false,
      dateTimeLabelFormats: {
        day: "%e " + this.translate.instant("of") + " %B",
        hour: "%e " + this.translate.instant("of") + " %B, %H:%M",
        week: this.translate.instant("week") + " %d " + this.translate.instant("of") + " %B %H:%M"
      }
    };
    highchartsOptions["scrollbar"] = SCROLLBAR_OPTIONS;
    highchartsOptions["plotOptions"]["series"]["marker"]["enabled"] = false;
    highchartsOptions["exporting"] = {
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "separator",
            "downloadCSV",
            "downloadXLS"
          ]
        }
      },
      filename:
        this.translate.instant("meters-export") + " " + this.DateParserService.getNow().format("ll")
    };

    this.highchartsOptions = highchartsOptions;
  }

  // Build chart options from GRAPH_CONFIG and attach the current series
  setChartsOptions(): void {
    let chartOptions: object = JSON.parse(JSON.stringify(GRAPH_CONFIG.default.chartOptions));
    delete chartOptions["chart"]["navigatorOptions"];
    chartOptions["legend"]["enabled"] = false;
    chartOptions["chart"]["height"] = "35%";

    let labelFormatSuffix: string;
    if (this.graphType === GRAPH_TYPES.INDEX || this.graphType === GRAPH_TYPES.CONSUMPTION) {
      labelFormatSuffix = " m³";
    } else if (this.graphType === GRAPH_TYPES.NORMALIZED_CONSUMPTION) {
      labelFormatSuffix = " Nm³";
    } else {
      labelFormatSuffix = " kWh";
    }
    chartOptions["yAxis"][0]["labels"]["format"] = "{value}" + labelFormatSuffix;

    let yAxisTitleText: string;
    if (this.graphType === GRAPH_TYPES.INDEX) {
      yAxisTitleText = this.translate.instant("value");
    } else if (this.graphType === GRAPH_TYPES.CONSUMPTION) {
      yAxisTitleText = this.translate.instant("consumption");
    } else {
      yAxisTitleText = this.translate.instant("consumption-normalized");
    }
    chartOptions["yAxis"][0]["title"]["text"] = yAxisTitleText;

    chartOptions["series"] = this.graphSeries;
    this.chartOptions = chartOptions;
    this.graphInitiated = true;
  }

  // Update the current CUPS
  updateCurrentCups(cupsId: number, cupsIndex: number): void {
    this.currentCupsId = cupsId;
    this.currentCupsIndex = cupsIndex;
    this.cupsContainer.nativeElement.style.setProperty("--cups-map-index", (cupsIndex + 1) * 55);
    this.SessionDataService.sendSubscriberCups(cupsId);
  }

  // Select meter on the map
  selectMeter(selectedMeter: any) {
    this.selectedMeterId = this.cupsList[this.currentCupsIndex]?.contadoresList.find(
      (meter: any) => meter.nroSerie === selectedMeter.nroSerie
    )?.id;
  }

  // Set chart type filters: gas meters use normalized consumption, others index and consumption
  updateGraphFilters(selectedMeter: any): void {
    let selectedMeterMetrology = this._cupsData.contadores?.find(
      meter => meter?.nroSerie === selectedMeter?.nroSerie
    )?.metrologyType;
    if (selectedMeterMetrology === METROLOGY_TYPE.GAS) {
      this.graphFilters = [
        {
          title: this.translate.instant("type"),
          options: [
            { value: GRAPH_TYPES.INDEX, name: this.translate.instant("index") },
            {
              value: GRAPH_TYPES.CONSUMPTION,
              name: this.translate.instant("hour-consumption")
            },
            {
              value: GRAPH_TYPES.NORMALIZED_CONSUMPTION,
              name: this.translate.instant("consumption-normalized")
            }
          ],
          selected: GRAPH_TYPES.NORMALIZED_CONSUMPTION
        }
      ];
      this.graphType = GRAPH_TYPES.NORMALIZED_CONSUMPTION;
    } else {
      this.graphFilters = [
        {
          title: this.translate.instant("type"),
          options: [
            { value: GRAPH_TYPES.INDEX, name: this.translate.instant("index") },
            {
              value: GRAPH_TYPES.CONSUMPTION,
              name: this.translate.instant("hour-consumption")
            }
          ],
          selected: GRAPH_TYPES.CONSUMPTION
        }
      ];
      this.graphType = GRAPH_TYPES.CONSUMPTION;
    }
  }
}
